import { useCallback, useEffect, useState } from 'react'
import type {
  AccountCategory,
  AccountType,
  AccountUse,
  BusinessEntity,
  FinancialAccount,
  Institution,
  ManualAccountInput,
  TaxTreatment,
} from '../../types/family-finance'
import {
  ACCOUNT_CATEGORY_ICONS,
  ACCOUNT_CATEGORY_LABELS,
  ACCOUNT_CATEGORY_ORDER,
  ACCOUNT_TYPE_LABELS,
  ACCOUNT_USE_LABELS,
  TAX_TREATMENT_LABELS,
  categoryOf,
} from '../../types/family-finance'
import {
  addManualAccount,
  claimSimpleFin,
  deleteAccount,
  disconnectInstitution,
  listAccounts,
  listEntities,
  listInstitutions,
  setAccountType,
  setAccountUse,
  syncInstitution,
  syncSimpleFin,
} from '../../lib/family-finance-api'
import {
  Card,
  Chip,
  EmptyState,
  LoadError,
  LoadingRows,
  errorMessage,
  inputClass,
  money,
  primaryBtn,
  relativeTime,
  secondaryBtn,
} from './shared'

// Ordered by category so the picker reads the same way the grouped view does.
const ACCOUNT_TYPES: AccountType[] = [
  'checking',
  'savings',
  'brokerage',
  'retirement',
  '529',
  'hsa',
  'credit',
  'mortgage',
  'property',
]

/** Money is right-aligned next to the account it belongs to; debts read red. */
function balanceClass(balance: number): string {
  return `font-medium tabular-nums ${
    balance < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'
  }`
}

// One <select> covers both questions ("personal or business?" and "whose
// books?") — `personal` or `business:<entityId>`, with an empty id for a
// business account not yet tied to an entity.
function useValueOf(account: Pick<FinancialAccount, 'use' | 'entityId'>): string {
  return account.use === 'business' ? `business:${account.entityId ?? ''}` : 'personal'
}

function parseUseValue(value: string): { use: AccountUse; entityId: string | null } {
  if (!value.startsWith('business')) return { use: 'personal', entityId: null }
  const id = value.slice('business:'.length)
  return { use: 'business', entityId: id || null }
}

function UseOptions({ entities }: { entities: BusinessEntity[] }) {
  return (
    <>
      <option value="personal">Personal</option>
      <optgroup label="Business">
        <option value="business:">Business — no entity yet</option>
        {entities.map((e) => (
          <option key={e.id} value={`business:${e.id}`}>
            {e.shortName || e.name}
          </option>
        ))}
      </optgroup>
    </>
  )
}

const miniSelectClass =
  'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs rounded-md px-1.5 py-0.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none'

/** Click the badge to re-file an account; saves as soon as a choice is made. */
function UseBadge({
  account,
  entities,
  onChanged,
}: {
  account: FinancialAccount
  entities: BusinessEntity[]
  onChanged: () => Promise<unknown> | void
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)

  async function apply(value: string) {
    const { use, entityId } = parseUseValue(value)
    setBusy(true)
    try {
      await setAccountUse(account.id, use, entityId)
      await onChanged()
    } finally {
      setBusy(false)
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <select
        autoFocus
        disabled={busy}
        value={useValueOf(account)}
        onChange={(e) => apply(e.target.value)}
        onBlur={() => setEditing(false)}
        className={miniSelectClass}
      >
        <UseOptions entities={entities} />
      </select>
    )
  }

  const entity = entities.find((e) => e.id === account.entityId)
  const label =
    account.use === 'business'
      ? `Business${entity ? ` · ${entity.shortName || entity.name}` : ''}`
      : ACCOUNT_USE_LABELS.personal

  return (
    <button
      onClick={() => setEditing(true)}
      title="Change whether this is a personal or business account"
      className="rounded-full hover:opacity-80 transition-opacity"
    >
      <Chip tone={account.use === 'business' ? 'purple' : 'slate'}>{label}</Chip>
    </button>
  )
}

const CATEGORY_TONES: Record<AccountCategory, 'blue' | 'green' | 'amber' | 'red' | 'slate'> = {
  bank: 'blue',
  investment: 'green',
  credit: 'amber',
  mortgage: 'red',
  property: 'slate',
}

/**
 * Click the type to re-file an account.
 *
 * SimpleFIN sends no account-type field, so the service guesses from the
 * account name — which is how a Schwab brokerage named "Individual …" lands in
 * Checking and never reaches the Investments view. The correction is stored by
 * the service and survives every later sync.
 */
function TypeBadge({
  account,
  onChanged,
}: {
  account: FinancialAccount
  onChanged: () => Promise<unknown> | void
}) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)

  async function apply(type: AccountType) {
    if (type === account.type) {
      setEditing(false)
      return
    }
    setBusy(true)
    setError(false)
    try {
      await setAccountType(account.id, type)
      await onChanged()
      setEditing(false)
    } catch {
      // Left open with the badge marked so a failed save is never mistaken
      // for a saved one.
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <select
        autoFocus
        disabled={busy}
        value={account.type}
        onChange={(e) => apply(e.target.value as AccountType)}
        onBlur={() => setEditing(false)}
        className={`${miniSelectClass} ${error ? 'border-red-500' : ''}`}
        title={error ? 'That change did not save — try again.' : undefined}
      >
        {ACCOUNT_TYPES.map((t) => (
          <option key={t} value={t}>
            {ACCOUNT_TYPE_LABELS[t]}
          </option>
        ))}
      </select>
    )
  }

  return (
    <button
      onClick={() => setEditing(true)}
      title="Change what kind of account this is"
      className="rounded-full hover:opacity-80 transition-opacity"
    >
      <Chip tone={CATEGORY_TONES[categoryOf(account.type)]}>{ACCOUNT_TYPE_LABELS[account.type]}</Chip>
    </button>
  )
}

/**
 * The bridge's own failures are terse ("HTTP 403 for POST /claim/…"), so pair
 * the raw message with what the owner should actually do about it.
 *
 * A 403 here has two very different causes and they are indistinguishable from
 * the status alone: a genuinely spent token, or the bridge's edge rejecting the
 * request before it reaches the claim handler. Naming only the first sent a
 * real, unclaimed token to the trash once — so say both.
 */
function claimHint(message: string): string | null {
  if (/HTTP 403/.test(message)) {
    return 'Two things cause this. Either the token is already spent (they are one-time), or the bridge rejected the request before it reached the claim handler — a server-side problem, not your token. Try the same token once more before generating a new one; if it fails again, keep it and check the service logs rather than burning another.'
  }
  if (/HTTP 40[0-9]/.test(message)) {
    return 'The bridge refused the claim. Try the same token once more before generating a new one — setup tokens are one-time, so a needless regeneration throws away a good one.'
  }
  if (/base64|claim URL/i.test(message)) {
    return 'That looks like the wrong value. The setup token is a long unbroken string of letters and digits — not the access URL and not your bridge login.'
  }
  if (/reach SimpleFIN/i.test(message)) {
    return 'The bridge did not answer. Nothing was consumed — wait a moment and try the same token again.'
  }
  // A network-level failure ("Failed to fetch", "Load failed", a gateway
  // timeout) means no answer came back — which says nothing about whether the
  // claim went through. The first pull from every bank can outlast the
  // gateway's ceiling, and the token is consumed before that pull starts.
  if (/failed to fetch|load failed|network|timeout|timed out/i.test(message)) {
    return 'No answer came back, so this does not tell us whether the claim succeeded — and the first balance pull can take longer than the request is allowed to wait. Do not generate a new token yet: close this, reload, and see whether your accounts appeared. If they did, the link worked and you only need Sync now.'
  }
  return null
}

function SimpleFinPanel({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)

  async function handleClaim() {
    if (!token.trim()) {
      setError('Paste the setup token from SimpleFIN Bridge first.')
      setHint(null)
      return
    }
    setBusy(true)
    setError(null)
    setHint(null)
    try {
      const result = await claimSimpleFin(token)
      // Linked, but the first balance pull didn't finish inside the request.
      // Keep the panel open and say so rather than closing onto an empty list.
      if (result.syncPending) {
        setToken('')
        setError('Linked — but the first balance pull is still running.')
        setHint('Your banks are connected and the token is used up; do not generate another. Give it a minute, then use Sync now to pull balances.')
        return
      }
      onDone()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Claim failed'
      setError(message)
      setHint(claimHint(message))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <ol className="text-slate-600 dark:text-slate-300 text-sm space-y-2 list-decimal list-inside bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <li>
          Create an account at{' '}
          <a href="https://bridge.simplefin.org" target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline">
            bridge.simplefin.org
          </a>{' '}
          (~$1.50/mo — you own the connection, not this app)
        </li>
        <li>Sign in to each bank/card there and authorize sharing</li>
        <li>Choose <span className="text-slate-900 dark:text-white">New App → Get Setup Token</span></li>
        <li>Paste the token below — it's one-time and exchanged server-side</li>
      </ol>
      <textarea
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Paste your SimpleFIN setup token…"
        rows={3}
        className={`${inputClass} font-mono text-xs`}
      />
      {error && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          <p className="text-amber-600 dark:text-amber-400 text-sm">{error}</p>
          {hint && <p className="text-amber-600/80 dark:text-amber-400/80 text-xs mt-1">{hint}</p>}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-slate-500 text-xs">
          Read-only balances. Revoke anytime at SimpleFIN or by disconnecting here.
        </p>
        <button onClick={handleClaim} disabled={busy} className={primaryBtn}>
          {busy ? 'Linking…' : 'Link accounts'}
        </button>
      </div>
    </div>
  )
}

function ManualAccountPanel({ entities, onDone }: { entities: BusinessEntity[]; onDone: () => void }) {
  const [form, setForm] = useState<ManualAccountInput>({
    name: '',
    institutionName: '',
    type: 'checking',
    taxTreatment: 'taxable',
    owner: 'Joint',
    balance: 0,
    use: 'personal',
    entityId: null,
  })
  const [balanceText, setBalanceText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    const balance = parseFloat(balanceText)
    if (!form.name.trim() || !form.institutionName.trim() || isNaN(balance)) {
      setError('Account name, institution, and a balance are required (negative for debts).')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await addManualAccount({ ...form, name: form.name.trim(), institutionName: form.institutionName.trim(), balance })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add account')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <input
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Account name (e.g. Truck Loan)"
        className={inputClass}
      />
      <input
        value={form.institutionName}
        onChange={(e) => setForm({ ...form, institutionName: e.target.value })}
        placeholder="Institution (e.g. Credit Union of TX)"
        className={inputClass}
      />
      <select
        value={form.type}
        onChange={(e) => setForm({ ...form, type: e.target.value as AccountType })}
        className={inputClass}
      >
        {ACCOUNT_TYPES.map((t) => (
          <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
        ))}
      </select>
      <select
        value={form.taxTreatment}
        onChange={(e) => setForm({ ...form, taxTreatment: e.target.value as TaxTreatment })}
        className={inputClass}
      >
        {(Object.keys(TAX_TREATMENT_LABELS) as TaxTreatment[]).map((t) => (
          <option key={t} value={t}>{TAX_TREATMENT_LABELS[t]}</option>
        ))}
      </select>
      <select
        value={useValueOf(form)}
        onChange={(e) => {
          const { use, entityId } = parseUseValue(e.target.value)
          const entity = entities.find((en) => en.id === entityId)
          setForm({
            ...form,
            use,
            entityId,
            // Naming the entity is almost always the right owner for its
            // account; still editable in the field below.
            owner: entity && (!form.owner || form.owner === 'Joint') ? entity.shortName || entity.name : form.owner,
          })
        }}
        className={inputClass}
      >
        <UseOptions entities={entities} />
      </select>
      <input
        value={form.owner}
        onChange={(e) => setForm({ ...form, owner: e.target.value })}
        placeholder="Owner (Joint / Nathanael / Meagan / entity)"
        className={inputClass}
      />
      <input
        value={balanceText}
        onChange={(e) => setBalanceText(e.target.value)}
        placeholder="Balance (negative for debts)"
        type="number"
        step="0.01"
        className={inputClass}
      />
      {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-2">{error}</p>}
      <div className="sm:col-span-2 flex justify-end">
        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy ? 'Adding…' : 'Add account'}
        </button>
      </div>
    </form>
  )
}

function ConnectModal({
  entities,
  onClose,
  onConnected,
}: {
  entities: BusinessEntity[]
  onClose: () => void
  onConnected: () => void
}) {
  const [mode, setMode] = useState<'choose' | 'simplefin' | 'manual'>('choose')

  function done() {
    onConnected()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-slate-900 dark:text-white font-semibold text-lg">
            {mode === 'choose' ? 'Add accounts' : mode === 'simplefin' ? 'Link banks via SimpleFIN' : 'Add a manual account'}
          </h3>
          <button onClick={onClose} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-xl leading-none">×</button>
        </div>

        {mode !== 'choose' && (
          <button onClick={() => setMode('choose')} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm mb-4">
            ← Other options
          </button>
        )}

        {mode === 'choose' && (
          <div className="space-y-3">
            <button
              onClick={() => setMode('simplefin')}
              className="w-full text-left p-4 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 transition-colors"
            >
              <p className="text-slate-900 dark:text-white font-medium">🔗 Link banks & cards (SimpleFIN)</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                You authorize each institution at SimpleFIN Bridge and paste one setup token here.
                Balances sync automatically; read-only and revocable.
              </p>
            </button>
            <button
              onClick={() => setMode('manual')}
              className="w-full text-left p-4 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-blue-500 transition-colors"
            >
              <p className="text-slate-900 dark:text-white font-medium">✍️ Add an account manually</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                Track anything without linking — property, loans, business accounts. Update the balance yourself.
              </p>
            </button>
            <div className="p-4 rounded-xl bg-slate-100/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-700/50">
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">🏦 Plaid <Chip tone="slate">optional, later</Chip></p>
              <p className="text-slate-500 text-xs mt-1">
                Polished in-app bank login used by big fintech apps. Needs a developer account + per-connection fees —
                SimpleFIN covers the same institutions for most personal use.
              </p>
            </div>
          </div>
        )}

        {mode === 'simplefin' && <SimpleFinPanel onDone={done} />}
        {mode === 'manual' && <ManualAccountPanel entities={entities} onDone={done} />}
      </div>
    </div>
  )
}

/** Same idea as claimHint, for the "refresh everything" path. */
function refreshHint(message: string): string | null {
  if (/not linked yet|409/.test(message)) {
    return 'No SimpleFIN connection is stored yet. Use “+ Add accounts” and paste a setup token first.'
  }
  if (/failed to fetch|load failed|network|timeout|timed out|502|504/i.test(message)) {
    return 'No answer came back. The pull may well have finished anyway — nothing is consumed by a refresh, so reload the page and look before trying again.'
  }
  if (/HTTP 40[0-9]|forbidden/i.test(message)) {
    return 'The bridge refused the request. If you revoked or regenerated access at SimpleFIN, link it again with a fresh setup token.'
  }
  return null
}

type RefreshNote = { tone: 'good' | 'plain' | 'bad'; text: string; hint: string | null }

type UseFilter = 'all' | AccountUse

// The second axis: what kind of account it is (see ACCOUNT_CATEGORY_ORDER).
type CategoryFilter = 'all' | AccountCategory

function inCategory(account: FinancialAccount, category: CategoryFilter): boolean {
  return category === 'all' || categoryOf(account.type) === category
}

// Institution answers "who holds this"; category answers "what do I have" —
// the question the owner is usually actually asking, and one no amount of
// filtering a bank-ordered list makes easy to read.
type GroupBy = 'institution' | 'category'

/** A filter pill carrying its own count and net balance. */
function FilterPill({
  label,
  rows,
  active,
  onClick,
}: {
  label: string
  rows: FinancialAccount[]
  active: boolean
  onClick: () => void
}) {
  const net = rows.reduce((sum, a) => sum + a.balance, 0)
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-lg border text-left transition-colors ${
        active
          ? 'bg-slate-200 dark:bg-slate-700 border-slate-300 dark:border-slate-600'
          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      <span className="text-slate-500 dark:text-slate-400 text-xs">
        {label} · {rows.length}
      </span>
      <span className={`block text-sm ${balanceClass(net)}`}>{money(net)}</span>
    </button>
  )
}

/**
 * One account line. Shared by both groupings, so an account reads the same way
 * wherever it is listed — with the institution named only in the category view,
 * where the card heading no longer carries it.
 */
function AccountRow({
  account,
  entities,
  showInstitution,
  busy,
  onChanged,
  onDelete,
}: {
  account: FinancialAccount
  entities: BusinessEntity[]
  showInstitution: boolean
  busy: boolean
  onChanged: () => Promise<unknown> | void
  onDelete: (account: FinancialAccount) => void
}) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 px-2 rounded-lg bg-slate-100/50 dark:bg-slate-900/50 group">
      <div className="min-w-0 mr-3">
        <p className="text-slate-800 dark:text-slate-200 truncate">
          {account.name} {account.mask !== '—' && <span className="text-slate-500">·· {account.mask}</span>}
        </p>
        <p className="text-slate-500 text-xs flex items-center gap-1.5 flex-wrap mt-0.5">
          {showInstitution && <span className="truncate">{account.institutionName}</span>}
          <span className="truncate">
            {account.owner} · {TAX_TREATMENT_LABELS[account.taxTreatment]}
          </span>
          {/* Always shown: it is also the control for moving an account into
              the right group, and a type that only appears on some rows is a
              control the owner can't find when they need it. */}
          <TypeBadge account={account} onChanged={onChanged} />
          <UseBadge account={account} entities={entities} onChanged={onChanged} />
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={balanceClass(account.balance)}>{money(account.balance, 2)}</span>
        <button
          onClick={() => onDelete(account)}
          disabled={busy}
          title="Remove this account from the dashboard"
          className="text-slate-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

export default function AccountsTab() {
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [entities, setEntities] = useState<BusinessEntity[]>([])
  const [filter, setFilter] = useState<UseFilter>('all')
  const [category, setCategory] = useState<CategoryFilter>('all')
  const [groupBy, setGroupBy] = useState<GroupBy>('category')
  const [loading, setLoading] = useState(true)
  const [showConnect, setShowConnect] = useState(false)
  const [syncing, setSyncing] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshNote, setRefreshNote] = useState<RefreshNote | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [busyAccount, setBusyAccount] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Returns the accounts it loaded so a caller can diff against what it had —
  // React state won't have settled by the time the await resolves.
  const reload = useCallback(async () => {
    const [insts, accts, ents] = await Promise.all([
      listInstitutions(),
      listAccounts(),
      listEntities(),
    ])
    setInstitutions(insts)
    setAccounts(accts)
    setEntities(ents)
    setLoading(false)
    return accts
  }, [])

  useEffect(() => {
    reload()
      .then(() => setLoadError(null))
      .catch((err) => {
        setLoadError(errorMessage(err))
        setLoading(false)
      })
  }, [reload])

  /**
   * Pull everything the bridge currently authorizes, then say what changed.
   * "Nothing new" is a real answer here and a confusing one — accounts linked
   * at the bridge still have to be shared with *this* connection — so it gets
   * the same weight as a failure.
   */
  async function handleRefreshAll() {
    setRefreshing(true)
    setRefreshNote(null)
    setSyncError(null)
    const before = new Set(accounts.map((a) => a.id))
    try {
      const result = await syncSimpleFin()
      const after = await reload()
      const added = after.filter((a) => !before.has(a.id))
      setRefreshNote({
        tone: added.length ? 'good' : 'plain',
        text: added.length
          ? `Found ${added.length} new account${added.length === 1 ? '' : 's'}: ${added
              .map((a) => a.name)
              .join(', ')}.`
          : `Up to date — ${result.accounts} account${result.accounts === 1 ? '' : 's'} across ${result.institutions} institution${result.institutions === 1 ? '' : 's'}, nothing new.`,
        hint: added.length
          ? null
          : 'If you just added accounts at SimpleFIN, they also have to be shared with this app’s connection — check the app/connection settings at bridge.simplefin.org, then refresh again.',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Refresh failed'
      setRefreshNote({ tone: 'bad', text: message, hint: refreshHint(message) })
    } finally {
      setRefreshing(false)
    }
  }

  async function handleSync(id: string) {
    setSyncing(id)
    setSyncError(null)
    try {
      await syncInstitution(id)
      await reload()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed'
      setSyncError(
        message.includes('501')
          ? 'Automatic sync isn’t available for this institution yet.'
          : message,
      )
    } finally {
      setSyncing(null)
    }
  }

  async function handleDisconnect(inst: Institution) {
    const accountCount = accounts.filter((a) => a.institutionId === inst.id).length
    if (
      !window.confirm(
        `Disconnect ${inst.name}? ${accountCount} linked account${accountCount === 1 ? '' : 's'} will be removed from the dashboard. (This does not close anything at the institution.)`,
      )
    )
      return
    await disconnectInstitution(inst.id)
    await reload()
  }

  async function handleDeleteAccount(account: FinancialAccount) {
    if (!window.confirm(`Remove "${account.name}" from the dashboard? The real account is untouched.`)) return
    setBusyAccount(account.id)
    try {
      await deleteAccount(account.id)
      await reload()
    } finally {
      setBusyAccount(null)
    }
  }

  if (loadError) {
    return (
      <LoadError
        error={loadError}
        onRetry={() => {
          setLoading(true)
          setLoadError(null)
          reload().catch((err) => {
            setLoadError(errorMessage(err))
            setLoading(false)
          })
        }}
      />
    )
  }
  if (loading) return <LoadingRows rows={5} />

  const hasLinked = institutions.some((i) => i.connectionType === 'linked')
  const matchesUse = (a: FinancialAccount) => filter === 'all' || a.use === filter
  const visible = accounts.filter((a) => matchesUse(a) && inCategory(a, category))
  const filtering = filter !== 'all' || category !== 'all'
  const total = (rows: FinancialAccount[]) => rows.reduce((sum, a) => sum + a.balance, 0)

  // Each row counts what the *other* row currently allows, so the totals on a
  // pill always describe what clicking it would actually show.
  const byCategory = accounts.filter((a) => inCategory(a, category))
  const byUse = accounts.filter(matchesUse)
  const USE_FILTERS: Array<{ id: UseFilter; label: string; rows: FinancialAccount[] }> = [
    { id: 'all', label: 'All', rows: byCategory },
    { id: 'personal', label: 'Personal', rows: byCategory.filter((a) => a.use === 'personal') },
    { id: 'business', label: 'Business', rows: byCategory.filter((a) => a.use === 'business') },
  ]
  const CATEGORY_FILTERS: Array<{ id: CategoryFilter; label: string; rows: FinancialAccount[] }> = (
    [
      { id: 'all', label: 'All types' },
      ...ACCOUNT_CATEGORY_ORDER.map((c) => ({ id: c, label: ACCOUNT_CATEGORY_LABELS[c] })),
    ] as Array<{ id: CategoryFilter; label: string }>
  ).map((c) => ({ ...c, rows: byUse.filter((a) => inCategory(a, c.id)) }))

  // The category view keeps every group on screen, empty ones included, so the
  // shape of the household's money is legible at a glance — "no mortgages" is
  // an answer, and a group that silently vanishes doesn't give it.
  const categoryGroups = ACCOUNT_CATEGORY_ORDER.map((id) => ({
    id,
    rows: visible.filter((a) => categoryOf(a.type) === id),
  })).filter((g) => g.rows.length > 0 || !filtering)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          {institutions.length} institutions · {accounts.length} accounts
        </p>
        <div className="flex items-center gap-2">
          {/* Picks up banks and accounts added at the bridge since the claim —
              a new institution has no card of its own to sync from. */}
          {hasLinked && (
            <button onClick={handleRefreshAll} disabled={refreshing} className={secondaryBtn}>
              {refreshing ? 'Refreshing…' : '↻ Refresh from SimpleFIN'}
            </button>
          )}
          <button onClick={() => setShowConnect(true)} className={primaryBtn}>
            + Add accounts
          </button>
        </div>
      </div>

      {refreshNote && (
        <div
          className={`rounded-lg px-3 py-2 border ${
            refreshNote.tone === 'bad'
              ? 'bg-amber-500/10 border-amber-500/30'
              : refreshNote.tone === 'good'
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : 'bg-slate-500/10 border-slate-500/30'
          }`}
        >
          <p
            className={`text-sm ${
              refreshNote.tone === 'bad'
                ? 'text-amber-600 dark:text-amber-400'
                : refreshNote.tone === 'good'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            {refreshNote.text}
          </p>
          {refreshNote.hint && (
            <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">{refreshNote.hint}</p>
          )}
        </div>
      )}

      {/* How the list is organized, before anything is filtered out of it. */}
      <div className="flex items-center gap-2">
        <span className="text-slate-500 dark:text-slate-400 text-xs">Organize by</span>
        <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          {(
            [
              { id: 'category', label: 'Type of account' },
              { id: 'institution', label: 'Institution' },
            ] as Array<{ id: GroupBy; label: string }>
          ).map((g) => (
            <button
              key={g.id}
              onClick={() => setGroupBy(g.id)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                groupBy === g.id
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Two independent axes: whose money it is, and what kind of account. */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {USE_FILTERS.map((f) => (
            <FilterPill
              key={f.id}
              label={f.label}
              rows={f.rows}
              active={filter === f.id}
              onClick={() => setFilter(f.id)}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_FILTERS.map((c) => (
            <FilterPill
              key={c.id}
              label={c.label}
              rows={c.rows}
              active={category === c.id}
              onClick={() => setCategory(c.id)}
            />
          ))}
        </div>
      </div>

      {syncError && (
        <p className="text-amber-600 dark:text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          {syncError}
        </p>
      )}

      {institutions.length === 0 && (
        <EmptyState
          icon="🏦"
          message="No accounts yet. Link your banks through SimpleFIN or add accounts manually to start tracking."
        />
      )}

      {/* Filtering hides whole institution cards, so say why the grid is empty. */}
      {institutions.length > 0 && visible.length === 0 && filtering && (
        <EmptyState icon="🔍" message="No accounts match this view. Try a different filter." />
      )}

      {groupBy === 'category' && institutions.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {categoryGroups.map((group) => {
            const groupTotal = total(group.rows)
            // Within a category the institution is the natural sub-order —
            // three cards from one bank should sit together.
            const rows = [...group.rows].sort(
              (a, b) =>
                a.institutionName.localeCompare(b.institutionName) ||
                a.name.localeCompare(b.name),
            )
            return (
              <Card key={group.id} className="!p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-2xl">{ACCOUNT_CATEGORY_ICONS[group.id]}</span>
                    <div className="min-w-0">
                      <p className="text-slate-900 dark:text-white font-medium text-sm truncate">
                        {ACCOUNT_CATEGORY_LABELS[group.id]}
                      </p>
                      <p className="text-slate-500 text-xs">
                        {rows.length} account{rows.length === 1 ? '' : 's'} ·{' '}
                        {new Set(rows.map((a) => a.institutionId)).size} institution
                        {new Set(rows.map((a) => a.institutionId)).size === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <span className={`text-sm ${balanceClass(groupTotal)}`}>{money(groupTotal, 2)}</span>
                </div>

                {rows.length === 0 ? (
                  <p className="text-slate-500 text-xs px-2 py-3">
                    Nothing filed here yet. If something belongs in this group, click its type on
                    another card to move it.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {rows.map((a) => (
                      <AccountRow
                        key={a.id}
                        account={a}
                        entities={entities}
                        showInstitution
                        busy={busyAccount === a.id}
                        onChanged={reload}
                        onDelete={handleDeleteAccount}
                      />
                    ))}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Per-institution sync and disconnect live only on this view — they are
          properties of the connection, not of any one account. */}
      {groupBy === 'institution' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {institutions.map((inst) => {
          const rows = visible.filter((a) => a.institutionId === inst.id)
          // Under a filter an institution with nothing matching is just noise.
          if (rows.length === 0 && filtering) return null
          const instTotal = total(rows)
          return (
            <Card key={inst.id} className="!p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="text-2xl">{inst.icon}</span>
                  <div className="min-w-0">
                    <p className="text-slate-900 dark:text-white font-medium text-sm truncate">{inst.name}</p>
                    <p className="text-slate-500 text-xs">
                      {inst.connectionType === 'manual' ? 'Manually tracked' : `Synced ${relativeTime(inst.lastSync)}`}
                      {inst.provider === 'simplefin' && ' · via SimpleFIN'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    {inst.status === 'connected' && <Chip tone="green">connected</Chip>}
                    {inst.status === 'manual' && <Chip tone="slate">manual</Chip>}
                    {inst.status === 'error' && <Chip tone="red">needs attention</Chip>}
                    {inst.status === 'pending' && <Chip tone="amber">pending</Chip>}
                  </div>
                  {rows.length > 0 && (
                    <span className={`text-sm ${balanceClass(instTotal)}`}>{money(instTotal, 2)}</span>
                  )}
                </div>
              </div>

              <div className="space-y-1.5 mb-3">
                {rows.map((a) => (
                  <AccountRow
                    key={a.id}
                    account={a}
                    entities={entities}
                    showInstitution={false}
                    busy={busyAccount === a.id}
                    onChanged={reload}
                    onDelete={handleDeleteAccount}
                  />
                ))}
              </div>

              {inst.connectionType === 'linked' && (
                <div className="flex gap-2">
                  <button onClick={() => handleSync(inst.id)} disabled={syncing === inst.id} className={secondaryBtn}>
                    {syncing === inst.id ? 'Syncing…' : '↻ Sync now'}
                  </button>
                  <button
                    onClick={() => handleDisconnect(inst)}
                    className="px-3 py-1.5 text-sm text-red-600/80 dark:text-red-400/80 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    Disconnect
                  </button>
                </div>
              )}
            </Card>
          )
        })}
      </div>
      )}

      {showConnect && (
        <ConnectModal
          entities={entities}
          onClose={() => setShowConnect(false)}
          onConnected={reload}
        />
      )}
    </div>
  )
}
